import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { PermissionCode } from '@app/shared/enums';
import { ApiService } from './api.service';
import { FeatureRequest, FeatureInfo, HeatDownloadInfo, HeatDataFileRequest, HeatDataFileRequestInfo } from '@app/shared/models/warmedaten/warmedaten-info';
import { catchError, map, switchMap } from 'rxjs/operators';
import { User } from '@app/shared/models/user';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class WarmedatenService extends ApiService {
  constructor(public http: HttpClient, private userService: UserService) {
    super(http);
  }

  baseUri = 'socrates/authz/api/municipalities';
  heatUri = 'socrates/meters/api/heatdata';

  public patch(currentMunicipalityGuid: string, featureRequest: FeatureRequest, warmedatenGuid: string): Observable<FeatureInfo> {
    return this.http.patch<FeatureInfo>(this.endpoint(this.baseUri, `${currentMunicipalityGuid}/restrictions/${warmedatenGuid}`), featureRequest);
  }

  public getRestrictionInfo(municipalityId: string): Observable<FeatureInfo> {
    return this.http
      .get<FeatureInfo[]>(
        this.endpoint(this.baseUri, `${municipalityId}/restrictions?$filter=permission/code eq '${PermissionCode.WARMEDATEN}' and isDeleted eq false`)
      )
      .pipe(map((response: FeatureInfo[]): FeatureInfo => response[0]));
  }

  public getUserEmailByRestriction(municipalityId: string): Observable<string> {
    return this.http
      .get<FeatureRequest[]>(
        this.endpoint(this.baseUri, `${municipalityId}/restrictions?$filter=permission/code eq '${PermissionCode.WARMEDATEN}' and isDeleted eq false`)
      )
      .pipe(
        switchMap((restrictions: FeatureRequest[]) => {
          if (restrictions.length > 0) {
            return this.userService.getByUserGuid(restrictions[0].featureRequestedByGUID).pipe(map((user: User) => user.email));
          }
          return of('');
        }),
        catchError(() => of(''))
      );
  }

  public getHeatDataZip(municipalityKey: string): Observable<HeatDownloadInfo> {
    return this.http
      .get<HeatDownloadInfo[]>(this.endpoint(this.heatUri, `${municipalityKey}?$filter=endswith(fileName, '.zip')&orderby=creationDate desc`))
      .pipe(map((response: HeatDownloadInfo[]) => response[0]));
  }

  public postHeatDataFileRequest(municipalityKey: string, post = ''): Observable<HeatDataFileRequest> {
    return this.http.post<HeatDataFileRequest>(this.endpoint(this.heatUri, `filerequests/${municipalityKey}`), post);
  }

  public getHeatDataFileRequest(municipalityKey: string, status: string): Observable<HeatDataFileRequestInfo[]> {
    let query = '$top=1&$orderby=utcRequestDate desc';
    if (!!status) {
      query = `$filter=status eq '${status}'&$top=1&$orderby=utcRequestDate desc`;
    }
    return this.http.get<HeatDataFileRequestInfo[]>(this.endpoint(this.heatUri, `filerequests/${municipalityKey}?${query}`));
  }
}
